<template>
  <Navbar 
    :showBackButton="true" 
    @back="goBack"
  />
  <div class="container body my-5 p-4 rounded shadow-sm">
    <h1 class="text-center mb-4 page-title">
      {{ $t("forms.driverRegistration.title") }}
    </h1>

    <!-- Show loading state -->
    <div v-if="loading" class="text-center my-5">
      <div class="spinner-border text-primary" role="status">
        <span class="visually-hidden">Loading...</span>
      </div>
      <p class="mt-3">{{ $t("forms.driverRegistration.loading") }}</p>
    </div>

    <!-- Show existing driver profile info -->
    <div v-else-if="isDriver && !isEditing" class="alert alert-info mb-4">
      <h5 class="alert-heading">{{ $t("forms.driverRegistration.alreadyDriver") }}</h5>
      <p>{{ $t("forms.driverRegistration.alreadyDriverMessage") }}</p>
      <button @click="loadDriverProfile" class="btn btn-info btn-sm me-2">
        {{ $t("forms.driverRegistration.viewProfile") }}
      </button>
      <button @click="startEditing" class="btn btn-warning btn-sm" style="margin-right: 5px; margin-left: 5px;">
        {{ $t("forms.driverRegistration.editProfile") }}
      </button>
      <button @click="goToDriverPanel" class="btn btn-info btn-sm">
        {{ $t("forms.driverRegistration.driverPanel") }}
      </button>
    </div>

    <!-- Driver Form -->
    <form v-if="!loading && (!isDriver || isEditing)" @submit.prevent="submitForm" style="background-color: var(--bg);">

      <!-- نمایش خطاهای سرور -->
      <div v-if="errorMessage" class="alert alert-danger alert-dismissible fade show" role="alert">
        {{ errorMessage }}
        <button type="button" class="btn-close" @click="errorMessage = ''"></button>
      </div>

      <!-- اطلاعات شخصی -->
      <fieldset class="border rounded p-3 mb-4">
        <legend class="float-none w-auto px-2 fs-5 fw-bold text-primary section-title">
          {{ $t("forms.driverRegistration.personalInfo") }}
        </legend>

        <div class="row gy-3">
          <div class="col-md-6">
            <label class="form-label">
              {{ $t("forms.driverRegistration.firstName") }}
            </label>
            <input
              v-model="driver.firstName"
              type="text"
              class="form-control"
              required
              disabled
              style="background-color: var(--bg);"
            />
            <small style="color: var(--text);">{{ $t("forms.driverRegistration.readOnlyField") }}</small>
          </div>

          <div class="col-md-6">
            <label class="form-label">
              {{ $t("forms.driverRegistration.lastName") }}
            </label>
            <input
              v-model="driver.lastName"
              type="text"
              class="form-control"
              required
              disabled
              style="background-color: var(--bg);"
            />
            <small style="color: var(--text);">{{ $t("forms.driverRegistration.readOnlyField") }}</small>
          </div>

          <div class="col-md-6">
            <label class="form-label">
              {{ $t("forms.driverRegistration.nationalId") }}
            </label>
            <input
              v-model="driver.nationalId"
              type="text"
              maxlength="10"
              class="form-control"
              required
              :disabled="submitting"
              @input="validateNationalId"
            />
            <div v-if="nationalIdError" class="text-danger small mt-1">
              {{ nationalIdError }}
            </div>
          </div>

          <div class="col-md-6">
            <label class="form-label">
              {{ $t("forms.driverRegistration.birthPlace") }}
            </label>
            <input
              v-model="driver.birthPlace"
              type="text"
              class="form-control"
              required
              :disabled="submitting"
            />
          </div>

          <div class="col-md-6">
            <label class="form-label">
              {{ $t("forms.driverRegistration.age") }}
            </label>
            <input
              v-model="driver.age"
              type="number"
              min="18"
              max="100"
              class="form-control"
              required
              :disabled="submitting"
            />
          </div>

          <div class="col-md-6">
            <label class="form-label">
              {{ $t("forms.driverRegistration.gender") }}
            </label>
            <select v-model="driver.gender" class="form-select" required :disabled="submitting">
              <option value="" disabled>{{ $t("forms.driverRegistration.selectGender") }}</option>
              <option value="male" class="options">
                {{ $t("forms.driverRegistration.male") }}
              </option>
              <option value="female" class="options">
                {{ $t("forms.driverRegistration.female") }}
              </option>
            </select>
          </div>
        </div>
      </fieldset>

      <!-- وسایل نقلیه -->
      <fieldset class="border rounded p-3 mb-4">
        <legend class="float-none w-auto px-2 fs-5 fw-bold text-success section-title">
          {{ $t("forms.driverRegistration.vehicleInfo") }}
        </legend>

        <div
          v-for="(vehicle, index) in driver.vehicles"
          :key="index"
          class="card mb-3 border-success"
        >
          <div class="card-header d-flex justify-content-between align-items-center">
            <span class="fw-bold text-success vehicle-title-text">
              {{ $t("forms.driverRegistration.vehicle") }} {{ index + 1 }}
            </span>
            <button
              type="button"
              class="btn btn-outline-danger btn-sm remove-btn"
              @click="removeVehicle(index)"
              :disabled="submitting"
            >
              {{ $t("forms.driverRegistration.removeVehicle") }}
            </button>
          </div>

          <div class="card-body" style="background-color: var(--bg);">
            <div class="row gy-3">
              <div class="col-md-6">
                <label class="form-label">
                  {{ $t("forms.driverRegistration.vehicleType") }}
                </label>
                <input
                  v-model="vehicle.type"
                  type="text"
                  class="form-control"
                  required
                  :disabled="submitting"
                />
              </div>

              <div class="col-md-6">
                <label class="form-label">
                  {{ $t("forms.driverRegistration.model") }}
                </label>
                <input
                  v-model="vehicle.model"
                  type="text"
                  class="form-control"
                  required
                  :disabled="submitting"
                />
              </div>

              <div class="col-md-6">
                <label class="form-label">
                  {{ $t("forms.driverRegistration.plate") }}
                </label>
                <input
                  v-model="vehicle.plate"
                  type="text"
                  class="form-control"
                  required
                  :disabled="submitting"
                  placeholder="مثال: ۱۲۳-۴۵۶-۷۸"
                />
              </div>

              <div class="col-md-6">
                <label class="form-label">
                  {{ $t("forms.driverRegistration.color") }}
                </label>
                <input
                  v-model="vehicle.color"
                  type="text"
                  class="form-control"
                  required
                  :disabled="submitting"
                />
              </div>
            </div>
          </div>
        </div>

        <button
          type="button"
          class="btn btn-success add-btn"
          @click="addVehicle"
          :disabled="submitting"
        >
          + {{ $t("forms.driverRegistration.addVehicle") }}
        </button>
      </fieldset>

      <!-- دکمه ثبت -->
      <button 
        type="submit" 
        class="btn btn-primary w-100 py-2 fw-bold submit-btn"
        :disabled="submitting || !isFormValid"
      >
        <span v-if="submitting" class="spinner-border spinner-border-sm me-2"></span>
        {{ isEditing ? $t("forms.driverRegistration.update") : $t("forms.driverRegistration.submit") }}
      </button>
    </form>

    <!-- Driver Profile Display -->
    <div v-if="isDriver && showProfile && !isEditing" class="mt-4">
      <div class="card">
        <div class="card-header bg-info text-white">
          <h5 class="mb-0">{{ $t("forms.driverRegistration.profileInfo") }}</h5>
        </div>
        <div class="card-body">
          <div class="row">
            <div class="col-md-6">
              <p><strong>{{ $t("forms.driverRegistration.firstName") }}:</strong> {{ driverProfile.firstName }}</p>
              <p><strong>{{ $t("forms.driverRegistration.lastName") }}:</strong> {{ driverProfile.lastName }}</p>
              <p><strong>{{ $t("forms.driverRegistration.nationalId") }}:</strong> {{ driverProfile.nationalId }}</p>
              <p><strong>{{ $t("forms.driverRegistration.birthPlace") }}:</strong> {{ driverProfile.birthPlace }}</p>
            </div>
            <div class="col-md-6">
              <p><strong>{{ $t("forms.driverRegistration.age") }}:</strong> {{ driverProfile.age }}</p>
              <p><strong>{{ $t("forms.driverRegistration.gender") }}:</strong> {{ driverProfile.gender === 'male' ? 'مرد' : 'زن' }}</p>
              <p><strong>{{ $t("forms.driverRegistration.vehiclesCount") }}:</strong> {{ driverProfile.vehicles?.length || 0 }}</p>
            </div>
          </div>
          
          <h6 class="mt-3">{{ $t("forms.driverRegistration.vehiclesList") }}</h6>
          <div class="table-responsive">
            <table class="table table-sm">
              <thead>
                <tr>
                  <th>{{ $t("forms.driverRegistration.vehicleType") }}</th>
                  <th>{{ $t("forms.driverRegistration.model") }}</th>
                  <th>{{ $t("forms.driverRegistration.plate") }}</th>
                  <th>{{ $t("forms.driverRegistration.color") }}</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(vehicle, idx) in driverProfile.vehicles" :key="idx">
                  <td>{{ vehicle.vehicle_type }}</td>
                  <td>{{ vehicle.model }}</td>
                  <td>{{ vehicle.plate_number }}</td>
                  <td>{{ vehicle.color }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>

    <div
      v-if="submitted"
      class="alert alert-success text-center mt-4 fw-bold success-alert"
    >
      {{ successMessage }}
    </div>
  </div>
</template>

<script>
import Navbar from '@/components/Navbar.vue';
import { useRouter } from 'vue-router'
import { peakService } from '@/services/api';
import { profileService } from '@/services/api'; // Import profile service to get customer info

export default {
  name: "DriverForm",
  components: {
    Navbar
  },
  setup() {
    const router = useRouter()

    const goBack = () => {
      router.push('/select-role')
    }

    const goToDriverPanel = () => {
      router.push('/peak/driver')
    }

    return {
    goToDriverPanel,
      goBack,
    }
  },
  data() {
    return {
      driver: {
        firstName: "",
        lastName: "",
        nationalId: "",
        birthPlace: "",
        age: "",
        gender: "",
        vehicles: [],
      },
      driverProfile: null,
      isDriver: false,
      isEditing: false,
      showProfile: false,
      loading: true,
      submitting: false,
      submitted: false,
      successMessage: "",
      errorMessage: "",
      nationalIdError: "",
    };
  },
  computed: {
    isFormValid() {
      // Basic validation - firstName and lastName come from customer profile, so they're always valid
      if (!this.driver.nationalId || !this.driver.birthPlace || 
          !this.driver.age || !this.driver.gender) {
        return false;
      }
      
      if (this.driver.vehicles.length === 0) {
        return false;
      }
      
      // Check all vehicles have required fields
      for (const vehicle of this.driver.vehicles) {
        if (!vehicle.type || !vehicle.model || !vehicle.plate || !vehicle.color) {
          return false;
        }
      }
      
      return true;
    }
  },
  mounted() {
    this.loadCustomerInfo();
  },
  methods: {
    async loadCustomerInfo() {
      try {
        this.loading = true;
        // First load customer profile to get first name and last name
        const customerResponse = await profileService.getCustomerProfile();
        const customerData = customerResponse.data.customer_data;
        
        // Set read-only fields from customer profile
        this.driver.firstName = customerData.first_name || "";
        this.driver.lastName = customerData.last_name || "";
        
        // Then check driver status
        await this.checkDriverStatus();
      } catch (error) {
        console.error('Error loading customer info:', error);
        this.errorMessage = this.$t("forms.driverRegistration.loadError");
        this.loading = false;
      }
    },
    
    async checkDriverStatus() {
      try {
        const response = await peakService.checkIsDriver();
        this.isDriver = response.data.is_driver;
        
        if (this.isDriver) {
          // Load existing driver profile
          await this.loadDriverProfile();
        }
      } catch (error) {
        console.error('Error checking driver status:', error);
        // If error (404 means not a driver), that's fine
        if (error.response?.status !== 404) {
          this.errorMessage = this.$t("forms.driverRegistration.checkError");
        }
      } finally {
        this.loading = false;
      }
    },
    
    async loadDriverProfile() {
      try {
        const response = await peakService.getDriverProfile();
        const result = response.data;
        
        // Populate form with existing data (firstName and lastName are already set from customer)
        if (result.driver_info) {
          const info = result.driver_info;
          this.driver = {
            ...this.driver, // Keep firstName and lastName from customer
            nationalId: info.national_id || "",
            birthPlace: info.birth_place || "",
            age: info.age || "",
            gender: info.gender || "",
            vehicles: result.vehicles?.map(v => ({
              type: v.vehicle_type,
              model: v.model,
              plate: v.plate_number,
              color: v.color
            })) || []
          };
        }
        
        // For profile display, use the data from the response
        this.driverProfile = {
          firstName: result.driver_info?.first_name || this.driver.firstName,
          lastName: result.driver_info?.last_name || this.driver.lastName,
          nationalId: result.driver_info?.national_id,
          birthPlace: result.driver_info?.birth_place,
          age: result.driver_info?.age,
          gender: result.driver_info?.gender,
          vehicles: result.vehicles
        };
        
        this.showProfile = true;
      } catch (error) {
        console.error('Error loading driver profile:', error);
        this.errorMessage = this.$t("forms.driverRegistration.loadError");
      }
    },
    
    startEditing() {
      this.isEditing = true;
      this.showProfile = false;
    },
    
    addVehicle() {
      this.driver.vehicles.push({
        type: "",
        model: "",
        plate: "",
        color: "",
      });
    },
    
    removeVehicle(index) {
      if (this.driver.vehicles.length > 1) {
        this.driver.vehicles.splice(index, 1);
      } else {
        this.errorMessage = this.$t("forms.driverRegistration.atLeastOneVehicle");
        setTimeout(() => this.errorMessage = "", 3000);
      }
    },
    
    validateNationalId() {
      const id = this.driver.nationalId;
      if (id && !/^\d{10}$/.test(id)) {
        this.nationalIdError = this.$t("forms.driverRegistration.invalidNationalId");
      } else {
        this.nationalIdError = "";
      }
    },
    
    async submitForm() {
      this.submitting = true;
      this.errorMessage = "";
      
      try {
        // Prepare data for API - note: firstName and lastName are NOT sent to backend
        // They are read from customer profile on the backend
        const formData = {
          nationalId: this.driver.nationalId,
          birthPlace: this.driver.birthPlace,
          age: parseInt(this.driver.age),
          gender: this.driver.gender,
          vehicles: this.driver.vehicles.map(v => ({
            type: v.type,
            model: v.model,
            plate: v.plate,
            color: v.color
          }))
        };
        
        let response;
        if (this.isEditing) {
          response = await peakService.updateDriverProfile(formData);
          this.successMessage = this.$t("forms.driverRegistration.updateSuccess");
        } else {
          response = await peakService.registerDriver(formData);
          this.successMessage = this.$t("forms.driverRegistration.success");
          this.isDriver = true;
        }
        
        this.submitted = true;
        
        // Reset editing mode and show profile
        this.isEditing = false;
        await this.loadDriverProfile();
        this.showProfile = true;
        
        // Clear form after 4 seconds
        setTimeout(() => {
          this.submitted = false;
          this.successMessage = "";
        }, 4000);
        
        console.log("Driver registration response:", response.data);
        
      } catch (error) {
        console.error("Error submitting driver form:", error);
        
        // Handle specific error messages
        if (error.response?.status === 400) {
          if (error.response?.data?.detail === "National ID is already registered") {
            this.errorMessage = this.$t("forms.driverRegistration.duplicateNationalId");
          } else if (error.response?.data?.detail === "Customer is already registered as a driver") {
            this.errorMessage = this.$t("forms.driverRegistration.alreadyDriver");
          } else {
            this.errorMessage = error.response?.data?.detail || this.$t("forms.driverRegistration.submitError");
          }
        } else if (error.response?.status === 401) {
          this.errorMessage = this.$t("forms.driverRegistration.unauthorized");
        } else {
          this.errorMessage = this.$t("forms.driverRegistration.networkError");
        }
      } finally {
        this.submitting = false;
      }
    }
  }
};
</script>

<style scoped>
/* راست‌چین کردن برای فارسی */
.container {
  direction: rtl;
  text-align: right;
  background-color: var(--bg);
}
.driver-form-page {
  padding: 40px 20px;
  display: flex;
  justify-content: center;
}

.driver-container {
  width: 100%;
  max-width: 900px;
  background: var(--card-color);
  border-radius: 14px;
  padding: 32px;
  box-shadow: var(--card-shadow);
}

.page-title {
  text-align: center;
  font-size: 26px;
  font-weight: 700;
  margin-bottom: 30px;
  color: var(--text);
}

.section-card {
  border: 1px solid var(--border-color);
  border-radius: 12px;
  padding: 20px;
  margin-bottom: 24px;
}

.section-title {
  font-size: 18px;
  font-weight: 600;
  color: var(--accent-color);
  padding: 0 8px;
}

.vehicle-title {
  color: var(--success-color);
}

.form-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit,minmax(220px,1fr));
  gap: 18px;
}

.form-group {
  display: flex;
  flex-direction: column;
}

.form-label {
  margin-bottom: 6px;
  font-size: 14px;
  color: var(--text);
}

.form-control {
  background-color: var(--bg);
  color: var(--text);
}

.form-control::placeholder {
  color: var(--text);
}

.form-input,
.form-select {
  border-radius: 8px;
  padding: 10px;
  color: var(--text);
  background-color: var(--bg);
}

.options {
  color: var(--text);
}

.form-input:focus,
.form-select:focus {
  outline: none;
  border-color: var(--accent-color);
}

.vehicle-card {
  border: 1px solid var(--success-color);
  border-radius: 10px;
  padding: 16px;
  margin-bottom: 16px;
  background: var(--card-hover);
}

.vehicle-header {
  display: flex;
  justify-content: space-between;
  margin-bottom: 12px;
}

.vehicle-title-text {
  font-weight: 600;
  color: var(--success-color);
}

.card-header {
  background-color: var(--card-bg);
}

.add-btn {
  margin-top: 10px;
  background: var(--success-color);
  border: none;
  padding: 10px 16px;
  border-radius: 8px;
  color: var(--text);
  cursor: pointer;
}

.remove-btn {
  background: transparent;
  border: 1px solid var(--danger-color);
  color: var(--text);
  border-radius: 6px;
  padding: 4px 10px;
  cursor: pointer;
}

.submit-btn {
  width: 100%;
  margin-top: 16px;
  background: var(--accent-color);
  color: white;
  border: none;
  padding: 14px;
  border-radius: 10px;
  font-weight: 600;
}

.success-alert {
  margin-top: 20px;
  padding: 12px;
  border-radius: 8px;
  background: var(--success-bg);
  color: var(--success-color);
  text-align: center;
}

</style>
